import { useRef, useState } from "react";
import { ApiError } from "../../../shared/api/client";
import { productionGateway } from "../production-gateway";
import type { ProductionCompletionCheck, ProductionDowntime, ProductionRun } from "../models";

export type OperatorRunActionsOptions = {
  selected: ProductionRun | null;
  identity: { terminalCode: string; sessionId: string; operatorId: string; lineId: string };
  notify: (message: string, tone?: "success" | "error") => void;
  onSessionMismatch: (previous: { sessionId: string; operatorId: string; lineId: string }) => Promise<"same-owner" | "changed-owner" | "unavailable">;
  refreshAuthoritativeRuns: (afterWrite?: boolean) => Promise<void>;
  load: (silent?: boolean, forceReference?: boolean) => Promise<void>;
  refreshHistory: () => Promise<unknown>;
  applyConfirmedDowntime: (runId: string, downtime: ProductionDowntime) => void;
};

const externalProductionChangeCodes = new Set([
  "PRODUCTION_STATE_CHANGED",
  "PRODUCTION_RUN_ACTIVE_ELSEWHERE",
  "PRODUCTION_DOWNTIME_CHANGED",
  "PRODUCTION_SESSION_CHANGED",
]);

export function useOperatorRunActionsWorkspace({ selected, identity, notify, onSessionMismatch, refreshAuthoritativeRuns, load, refreshHistory, applyConfirmedDowntime }: OperatorRunActionsOptions) {
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionCheck, setCompletionCheck] = useState<ProductionCompletionCheck | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [externalChange, setExternalChange] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const criticalSavingRef = useRef(false);
  const recoverSessionMismatch = async () => {
    const outcome = await onSessionMismatch({ sessionId: identity.sessionId, operatorId: identity.operatorId, lineId: identity.lineId });
    const message = outcome === "same-owner"
      ? "Oturum yenilendi. İşlem tekrarlanmadı; duruş taslağını kontrol edip yeniden kaydedin."
      : outcome === "changed-owner"
        ? "Bu terminalde başka bir oturum açıldı. Taslağınız yalnız kendi oturumunuza bağlı olarak korundu; kaydı otomatik yeniden göndermedik."
        : "Etkin oturum doğrulanamadı. Taslaklar silinmedi; bağlantınızı ve kendi PIN oturumunuzu kontrol edin.";
    notify(message, "error");
    return message;
  };
  const transact = async (
    work: () => Promise<unknown>,
    message: string,
    options: { rethrow?: boolean } = {},
  ) => {
    if (criticalSavingRef.current) return false;
    criticalSavingRef.current = true;
    setSaving(true);
    setSaveFailed(false);
    setOperationError(null);
    try {
      await work();
      notify(message);
      // Mutation acknowledgement is durable, but a full workspace refresh may
      // still wait for reference/ERP metadata. Refresh the run ledger on its
      // own lane so stop/start state reaches the tablet without that delay.
      void refreshAuthoritativeRuns(true);
      void load(true, false);
      void refreshHistory();
      setExternalChange(null);
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      setSaveFailed(true);
      let failureMessage = error instanceof Error ? error.message : "İşlem tamamlanamadı.";
      if (error instanceof ApiError && error.code === "PRODUCTION_SESSION_CHANGED") {
        const recovery = await recoverSessionMismatch();
        setExternalChange(recovery);
        failureMessage = recovery;
      } else if (error instanceof ApiError && externalProductionChangeCodes.has(error.code)) {
        setExternalChange("Bu iş başka bir terminal veya oturumda değişti. Ekrandaki bilgiler sunucudan güncellendi; işlem otomatik olarak tekrarlanmadı.");
        void load(true);
      }
      if (error instanceof ApiError && error.code === "PLC_M0_UNCONFIRMED") {
        setExternalChange("PLC M0 durumu doğrulanamadı. İş kaydı güncellenmiş olabilir; makineyi kontrol edin. Komutu otomatik tekrarlamayın.");
        void load(true);
      }
      setOperationError(failureMessage);
      notify(failureMessage, "error");
      if (options.rethrow) throw error;
      return false;
    } finally {
      criticalSavingRef.current = false;
      setSaving(false);
    }
  };

  const openCompletionCheck = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      setCompletionCheck(await productionGateway.completionCheck(selected.id, identity.terminalCode));
      setCompletionOpen(true);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Tamamlama kontrolü alınamadı.");
    } finally {
      setSaving(false);
    }
  };

  const acknowledgePlanNote = () => selected
    ? transact(() => productionGateway.acknowledgePlanNote(selected.id, identity.terminalCode), "Ürün notu okundu olarak kaydedildi.")
    : Promise.resolve(false);
  const startDowntime = () => selected
    ? transact(() => productionGateway.startDowntime(selected.id, {
      startedAt: new Date().toISOString(), idempotencyKey: crypto.randomUUID(),
    }, identity.terminalCode).then((downtime) => { applyConfirmedDowntime(selected.id, downtime); return downtime; }), "Duruş başlatıldı.")
    : Promise.resolve(false);
  const stopDowntime = (downtimeId: string) => selected
    ? transact(() => productionGateway.stopDowntime(selected.id, downtimeId, "", identity.terminalCode)
      .then((downtime) => { applyConfirmedDowntime(selected.id, downtime); return downtime; }), "Duruş bitirildi; üretim sayacı devam ediyor.")
    : Promise.resolve(false);
  const reportComplaint = (description: string) => selected
    ? transact(() => productionGateway.reportComplaint(selected.id, description, identity.terminalCode), "Şikayet vardiya amirine gönderildi.")
    : Promise.resolve(false);
  const voidQuantity = (entryId: string, reason: string) => selected
    ? transact(() => productionGateway.voidQuantity(selected.id, entryId, reason, undefined, identity.terminalCode), "Hatalı kayıt geri alındı.")
    : Promise.resolve(false);
  const completeRun = (reason: string) => selected && completionCheck
    ? transact(() => productionGateway.completeRun(selected.id, reason, completionCheck.version, identity.terminalCode, completionCheck.machineCountedBoxes, completionCheck.completedCases?.runValue), "İş tamamlandı.")
    : Promise.resolve(false);

  return {
    completionOpen, completionCheck, lastSavedAt, saveFailed, operationError, externalChange, saving,
    setCompletionOpen, setCompletionCheck, setLastSavedAt, setSaveFailed, setOperationError, setExternalChange,
    transact, recoverSessionMismatch, openCompletionCheck,
    acknowledgePlanNote, startDowntime, stopDowntime, reportComplaint, voidQuantity, completeRun,
  };
}

export type OperatorRunActionsWorkspace = ReturnType<typeof useOperatorRunActionsWorkspace>;
